import React, { useEffect, useRef, useState, MouseEvent, JSX } from 'react';
import useWindow from '../../Hooks/useWindow';
import { faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ScrollTabs = ({
  anchors,
  scrollOffset
}: {
  anchors: { label: string; id: string }[];
  scrollOffset?: number;
}): JSX.Element => {
  const window = useWindow();
  const navOuter = useRef<HTMLDivElement>(null);
  const navInner = useRef<HTMLDivElement>(null);
  const navContent = useRef<HTMLUListElement>(null);
  const [buttonVisibility, setButtonVisibility] = useState<{ left: boolean; right: boolean }>({
    left: false,
    right: false
  });
  const [activeAnchor, setActiveAnchor] = useState<string | undefined>(undefined);

  const checkVisibility = (): void => {
    const navContentWidth = navContent?.current?.getBoundingClientRect()?.width || 0;
    const navOuterWidth = navOuter?.current?.getBoundingClientRect()?.width || 0;
    if (navContentWidth > navOuterWidth) {
      let newButtonVisibility = { left: true, right: true };
      if (navInner?.current?.scrollLeft === 0) {
        newButtonVisibility = { ...newButtonVisibility, left: false };
      }
      if ((navInner?.current?.scrollLeft || 0) >= navContentWidth - navOuterWidth - 1) {
        newButtonVisibility = { ...newButtonVisibility, right: false };
      }
      setButtonVisibility(newButtonVisibility);
    } else {
      setButtonVisibility({ left: false, right: false });
    }
  };

  const checkActiveAnchor = (): void => {
    setActiveAnchor(
      anchors
        .map(anchor => {
          const anchorElement = window?.document?.getElementById(anchor.id);
          if (!!anchorElement) {
            const anchorPosition = anchorElement.getBoundingClientRect().top;
            const offset = anchorPosition + (window?.pageYOffset || 0) - (scrollOffset || 0);
            return { id: anchor.id, offset };
          } else {
            return { id: anchor.id, offset: 0 };
          }
        })
        .filter(anchor => (window?.pageYOffset || 0) >= anchor.offset - 10)
        .reduce(
          (
            total: { id: string; offset: number } | undefined,
            current: { id: string; offset: number }
          ) => {
            if (!!total && current.offset < total.offset) {
              return { ...total };
            } else {
              return { ...current };
            }
          },
          undefined
        )?.id
    );
  };

  const handleScroll = (): void => {
    checkVisibility();
  };

  const handleWindowScroll = (): void => {
    checkActiveAnchor();
  };

  const initScroll = (): void => {
    navInner?.current?.removeEventListener('scroll', handleScroll);
    navInner?.current?.addEventListener('scroll', handleScroll);
    handleScroll();
  };

  const handleNavClick = (isLeft: boolean): void => {
    navInner?.current?.scrollBy({
      top: 0,
      left: (navOuter?.current?.getBoundingClientRect()?.width || 0) * (isLeft ? -1 : 1),
      behavior: 'smooth'
    });
    checkVisibility();
  };

  const handleAnchorClick = (e: MouseEvent<HTMLAnchorElement>, id: string): void => {
    e.preventDefault();
    const anchorElement = window?.document?.getElementById(id);
    if (!!anchorElement) {
      const anchorPosition = anchorElement.getBoundingClientRect().top;
      const offset = anchorPosition + (window?.pageYOffset || 0) - (scrollOffset || 0);
      window?.scrollTo({ behavior: 'smooth', top: offset });
    }
  };

  useEffect(() => {
    window?.addEventListener('resize', initScroll);
    window?.addEventListener('scroll', handleWindowScroll);
    initScroll();
    checkVisibility();
    checkActiveAnchor();

    return () => {
      window?.removeEventListener('resize', initScroll);
      window?.removeEventListener('scroll', handleWindowScroll);
    };
  }, [navContent, navInner, navOuter]);

  return (
    <nav className="anchorList">
      <div className="nav__outer" ref={navOuter}>
        {buttonVisibility.left && (
          <span className="nav__button__container nav__button__container--left">
            <FontAwesomeIcon
              icon={faCaretLeft}
              className="nav__button nav__button--left"
              onClick={(): void => handleNavClick(true)}
            />
          </span>
        )}
        <div className="nav__inner" ref={navInner}>
          <ul className="nav" ref={navContent}>
            {anchors.map(anchor => (
              <li key={anchor.id} className="nav-item anchorList__anchor">
                <a
                  className={`nav-link anchorList__link${
                    anchor.id === activeAnchor ? ' active' : ''
                  }`}
                  href={`#${anchor.id}`}
                  onClick={(e: MouseEvent<HTMLAnchorElement>): void =>
                    handleAnchorClick(e, anchor.id)
                  }
                >
                  {anchor.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        {buttonVisibility.right && (
          <span className="nav__button__container nav__button__container--right">
            <FontAwesomeIcon
              icon={faCaretRight}
              className="nav__button nav__button--right"
              onClick={(): void => handleNavClick(false)}
            />
          </span>
        )}
      </div>
    </nav>
  );
};

export default ScrollTabs;
